/**
 * Calculate Victory Points from IMPs
 * Using the WBF/EBU formula with golden mean
 */

const TAU = (Math.sqrt(5) - 1) / 2; // Golden mean ≈ 0.618

/**
 * Calculate VP for winner given margin and number of boards
 * @param {number} margin - IMP margin (absolute value)
 * @param {number} numBoards - Number of boards played
 * @returns {number} VP for winner (rounded to 2 decimals, truncated)
 */
export function calculateVP(margin, numBoards) {
  const M = Math.abs(margin);
  const B = 15 * Math.sqrt(numBoards);
  
  // Maximum VP
  if (M >= B) {
    return 20;
  }
  
  // Calculate VP using the formula
  const exponent = (3 * M) / B;
  const numerator = 1 - Math.pow(TAU, exponent);
  const denominator = 1 - Math.pow(TAU, 3);
  
  let vp = 10 + 10 * (numerator / denominator);
  
  // Truncate to 2 decimals
  vp = Math.floor(vp * 100) / 100;
  
  // Ensure maximum of 20
  if (vp > 20) vp = 20;
  
  return vp;
}

/**
 * Calculate both winner and loser VPs
 * @param {number} margin - IMP margin (positive = team1 wins, negative = team2 wins)
 * @param {number} numBoards - Number of boards played
 * @returns {Object} {team1VP, team2VP}
 */
export function calculateVPs(margin, numBoards) {
  const absMargin = Math.abs(margin);
  const winnerVP = calculateVP(absMargin, numBoards);
  const loserVP = Math.floor((20 - winnerVP) * 100) / 100; // Truncate loser VP too
  
  if (margin > 0) {
    return { team1VP: winnerVP, team2VP: loserVP };
  } else if (margin < 0) {
    return { team1VP: loserVP, team2VP: winnerVP };
  } else {
    // Draw
    return { team1VP: 10, team2VP: 10 };
  }
}

/**
 * Validate and adjust VPs to ensure monotonicity
 * V(i+1) - V(i) <= V(i) - V(i-1)
 * @param {Array} vpTable - Array of {margin, vp} objects
 * @returns {Array} Adjusted VP table
 */
export function validateVPTable(vpTable) {
  const adjusted = [...vpTable];
  let changed = true;
  let iterations = 0;
  const maxIterations = 4;
  
  while (changed && iterations < maxIterations) {
    changed = false;
    iterations++;
    
    for (let i = 1; i < adjusted.length - 1; i++) {
      const prev = adjusted[i - 1].vp;
      const curr = adjusted[i].vp;
      const next = adjusted[i + 1].vp;
      
      const diff1 = curr - prev;
      const diff2 = next - curr;
      
      // Check if monotonicity is violated
      if (diff2 > diff1) {
        adjusted[i].vp = Math.floor((adjusted[i].vp + 0.01) * 100) / 100;
        changed = true;
      }
    }
  }
  
  return adjusted;
}

/**
 * Generate VP table for a given number of boards
 * @param {number} numBoards - Number of boards
 * @returns {Array} VP table with margins and VPs
 */
export function generateVPTable(numBoards) {
  const B = 15 * Math.sqrt(numBoards);
  const maxMargin = Math.ceil(B);
  
  const table = [];
  
  for (let margin = 0; margin <= maxMargin; margin++) {
    const vp = calculateVP(margin, numBoards);
    table.push({ margin, vp });
  }
  
  // Validate and adjust for monotonicity
  return validateVPTable(table);
}

/**
 * Get VP from pre-calculated table (for efficiency)
 * @param {number} margin - IMP margin
 * @param {number} numBoards - Number of boards
 * @returns {Object} {winnerVP, loserVP}
 */
export function getVPFromTable(margin, numBoards) {
  const table = generateVPTable(numBoards);
  const absMargin = Math.abs(margin);
  
  let vp = 20; // Default for margins >= B
  
  // Find VP in table
  for (const entry of table) {
    if (absMargin === entry.margin) {
      vp = entry.vp;
      break;
    } else if (absMargin < entry.margin) {
      // Interpolate or use previous value
      vp = table[Math.max(0, table.indexOf(entry) - 1)].vp;
      break;
    }
  }
  
  const loserVP = Math.floor((20 - vp) * 100) / 100;
  
  if (margin > 0) {
    return { winnerVP: vp, loserVP };
  } else if (margin < 0) {
    return { winnerVP: loserVP, loserVP: vp };
  } else {
    return { winnerVP: 10, loserVP: 10 };
  }
}
